/* cp-outubro-rosa-gift-v1 + cp-cartelinha-gems-v1. Canonical storefront source; no checkout/server enforcement. */
(() => {
  'use strict';
  if (globalThis.CPPapayaPinkGift) return;
  // Campaign ended on 2026-10-27. Keep reconciliation to remove gifts from older carts.
  const PINK_GIFT_ENABLED = globalThis.CPPapayaPinkGiftConfig?.enabled === true;
  const IDS = Object.freeze({
    caseProduct: '8285250977957',
    strapProducts: Object.freeze(['8285151953061', '8285185573029', '8869390876837']),
    giftProduct: '10804626718885',
    giftVariant: '50587452801189'
  });
  const productId = (item) => String(item.product_id ?? item.product?.id ?? '');
  const variantId = (item) => String(item.variant_id ?? item.variant?.id ?? item.id ?? '');
  const isGift = (item) => productId(item) === IDS.giftProduct || variantId(item) === IDS.giftVariant;
  const positiveQuantity = (item) => Number.isSafeInteger(Number(item.quantity)) && Number(item.quantity) > 0;
  const isPaid = (item) => Number(item.final_line_price ?? item.line_price ?? item.final_price ?? item.price ?? 0) > 0;
  const gemsInput = globalThis.CPPapayaGemsGiftConfig;
  const GEMS = gemsInput && /^\d+$/.test(String(gemsInput.productId)) && /^\d+$/.test(String(gemsInput.variantId)) &&
    /^[a-z0-9][a-z0-9-]*$/.test(String(gemsInput.productHandle))
    ? Object.freeze({ productId: String(gemsInput.productId), variantId: String(gemsInput.variantId), productHandle: String(gemsInput.productHandle) }) : null;
  const isGemsGift = (item, config) => Boolean(config && (productId(item) === String(config.productId) || variantId(item) === String(config.variantId)));

  function planGift(cart) {
    if (!Array.isArray(cart?.items)) throw new Error('Carrinho inválido.');
    let hasCase = false, hasStrap = false;
    const inspect = (item, paidParent = false) => {
      if (!positiveQuantity(item) || isGift(item)) return;
      const paid = paidParent || isPaid(item);
      if (paid && productId(item) === IDS.caseProduct) hasCase = true;
      if (paid && IDS.strapProducts.includes(productId(item))) hasStrap = true;
      // Native bundles expose their component products; the paid parent establishes payment.
      for (const component of item.item_components || []) inspect(component, paid);
    };
    cart.items.forEach((item) => inspect(item));
    const eligible = PINK_GIFT_ENABLED && hasCase && hasStrap;
    const gifts = cart.items.filter((item) => positiveQuantity(item) && isGift(item));
    const keeper = eligible ? gifts.find((item) => variantId(item) === IDS.giftVariant) : null;
    const updates = {};
    for (const item of gifts) {
      const quantity = item === keeper ? 1 : 0;
      if (Number(item.quantity) !== quantity) {
        if (!item.key) throw new Error('O brinde está sem identificador de carrinho.');
        updates[item.key] = quantity;
      }
    }
    return { eligible, hasCase, hasStrap, giftQuantity: gifts.reduce((sum, item) => sum + Number(item.quantity), 0),
      updates, add: eligible && !keeper ? { id: IDS.giftVariant, quantity: 1, properties: { _cp_outubro_rosa: '2026' } } : null };
  }

  function eligibilityFingerprint(cart, gemsConfig = null) {
    return JSON.stringify(cart.items.filter((item) => !isGift(item) && !isGemsGift(item, gemsConfig)).map((item) =>
      [item.key, item.quantity, item.final_line_price ?? item.line_price, item.item_components || null]));
  }

  function planGemsGift(cart, config = GEMS, available = true) {
    if (!Array.isArray(cart?.items)) throw new Error('Carrinho inválido.');
    if (!config) return { eligible: false, hasPaidProduct: false, giftQuantity: 0, updates: {}, add: null };
    // An order paid with a full-value coupon still receives its gift. Gifts themselves never qualify.
    const isMerchandise = (item) => positiveQuantity(item) && !isGift(item) && !isGemsGift(item, config) &&
      (Number(item.original_line_price ?? item.original_price ?? item.final_line_price ?? item.line_price ?? item.final_price ?? item.price ?? 0) > 0 ||
        (item.item_components || []).some(isMerchandise));
    const hasPaidProduct = cart.items.some(isMerchandise);
    const eligible = hasPaidProduct && available !== false;
    const gifts = cart.items.filter((item) => positiveQuantity(item) && isGemsGift(item, config));
    const keeper = eligible ? gifts.find((item) => variantId(item) === String(config.variantId)) : null;
    const updates = {};
    for (const item of gifts) {
      const quantity = item === keeper ? 1 : 0;
      if (Number(item.quantity) !== quantity) {
        if (!item.key) throw new Error('O brinde está sem identificador de carrinho.');
        updates[item.key] = quantity;
      }
    }
    return { eligible, hasPaidProduct, giftQuantity: gifts.reduce((sum, item) => sum + Number(item.quantity), 0), updates,
      add: eligible && !keeper ? { id: String(config.variantId), quantity: 1, properties: { _cp_cartelinha_gems: '2026' } } : null };
  }

  function hasPromotionItems(cart) {
    const plan = planGift(cart);
    const gemsPlan = planGemsGift(cart);
    return plan.hasCase || plan.hasStrap || plan.giftQuantity > 0 || gemsPlan.hasPaidProduct || gemsPlan.giftQuantity > 0;
  }

  // Shopify's asynchronous cart listener identifies additions by response.url,
  // including on clones. A synthesized body must retain the network metadata.
  function retainResponseMetadata(response, original) {
    const clone = response.clone.bind(response);
    Object.defineProperties(response, {
      url: { value: original.url },
      type: { value: original.type },
      redirected: { value: original.redirected },
      clone: { value: () => retainResponseMetadata(clone(), original) }
    });
    return response;
  }

  // One queue owns all observed cart writes and gift reconciliation. Adapters are testable without a DOM.
  function createCoordinator(adapter) {
    const gemsConfig = adapter.gems || null;
    let tail = Promise.resolve(), pending = 0, lastCart = null, unavailableFingerprint = null;
    const enqueue = (work) => {
      pending += 1;
      const result = tail.then(work);
      tail = result.catch(() => {}).finally(() => { pending -= 1; });
      return result;
    };
    const read = async () => {
      try {
        const cart = await adapter.readCart();
        planGift(cart);
        lastCart = cart;
        adapter.observed?.(lastCart);
        return lastCart;
      } catch (error) {
        lastCart = null;
        adapter.invalidated?.();
        throw error;
      }
    };
    const reconcilePink = async () => {
      let cart = await read(), changed = false, unavailable = false;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        const plan = planGift(cart);
        if (Object.keys(plan.updates).length) {
          // Never send notes, attributes, or other cart lines in a gift-only update.
          try { await adapter.updateGift(plan.updates); }
          catch (error) {
            // A timed-out write may have succeeded; reconcile once, never blindly replay.
            cart = await read();
            if (Object.keys(planGift(cart).updates).length) throw error;
          }
          changed = true;
          cart = await read();
          continue;
        }
        if (!plan.add) return { cart, changed, unavailable };
        const fingerprint = eligibilityFingerprint(cart, gemsConfig);
        if (unavailableFingerprint === fingerprint) return { cart, changed, unavailable: true };
        try {
          await adapter.addGift(plan.add);
          unavailableFingerprint = null;
          changed = true;
        } catch (error) {
          // Paid products remain purchased successfully when the promotional item is unavailable.
          cart = await read();
          const currentPlan = planGift(cart);
          if (currentPlan.add && !Object.keys(currentPlan.updates).length) {
            // Only a provider rejection is stable for this cart. Network errors
            // must be retried on the next synchronization or checkout.
            unavailableFingerprint = error.status === 422 ? eligibilityFingerprint(cart, gemsConfig) : null;
            return { cart, changed, unavailable: true };
          }
          if (!currentPlan.eligible && currentPlan.giftQuantity === 0) return { cart, changed, unavailable: false };
          changed = true;
        }
        cart = await read();
      }
      throw new Error('Não foi possível ajustar o brinde. Confira o carrinho e tente novamente.');
    };
    const reconcile = async () => {
      const pink = await reconcilePink();
      if (!gemsConfig) return pink;
      let cart = pink.cart, changed = pink.changed, available = null;
      const initial = planGemsGift(cart, gemsConfig);
      if (!initial.hasPaidProduct && !initial.giftQuantity) return { ...pink, gemsAvailable: null };
      // Recheck at every synchronization and checkout: cart lines do not reserve stock.
      // A failed lookup is unknown, not sold out. The add API can still enforce inventory.
      try { available = await adapter.readGemsAvailability(); }
      catch { available = null; }
      if (available !== true && available !== false) available = null;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        const plan = planGemsGift(cart, gemsConfig, available);
        if (Object.keys(plan.updates).length) {
          try { await adapter.updateGift(plan.updates); }
          catch (error) {
            cart = await read();
            if (Object.keys(planGemsGift(cart, gemsConfig, available).updates).length) throw error;
          }
          changed = true;
          cart = await read();
          continue;
        }
        if (!plan.add) return { ...pink, cart, changed, gemsAvailable: available };
        try {
          await adapter.addGift(plan.add);
          changed = true;
        } catch (error) {
          // A timed-out addition may have succeeded. Read back before deciding to retry.
          cart = await read();
          const after = planGemsGift(cart, gemsConfig, available);
          if (after.add && !Object.keys(after.updates).length) {
            return { ...pink, cart, changed, gemsAvailable: error.status === 422 ? false : available };
          }
          changed = true;
        }
        cart = await read();
      }
      throw new Error('Não foi possível ajustar o brinde. Confira o carrinho e tente novamente.');
    };
    return Object.freeze({
      sync: (finish = (result) => result) => enqueue(async () => finish(await reconcile())),
      mutate: (perform, finish = (result) => result) => enqueue(async () => {
        // A failed request may already have changed Shopify. Never reuse a
        // snapshot from before this mutation to replace a response or event.
        lastCart = null;
        adapter.invalidated?.();
        let response;
        try { response = await perform(); }
        catch (error) {
          try { await reconcile(); } catch { lastCart = null; adapter.invalidated?.(); }
          throw error;
        }
        // A failed/partially accepted cart write can still change Shopify's cart.
        let result;
        try { result = { response, ...(await reconcile()) }; }
        catch (error) { lastCart = null; adapter.invalidated?.(); result = { response, cart: null, error }; }
        return finish(result);
      }),
      checkout: (proceed) => enqueue(async () => {
        const result = await reconcile();
        if (Object.keys(planGift(result.cart).updates).length ||
          Object.keys(planGemsGift(result.cart, gemsConfig, result.gemsAvailable).updates).length) throw new Error('Ajuste o brinde antes de finalizar.');
        // Invoke navigation inside the queue so a subsequent mutation cannot slip between check and submit.
        await proceed(result);
        return result;
      }),
      state: () => ({ pending, cart: lastCart }),
      retryGift: () => { unavailableFingerprint = null; }
    });
  }

  const api = { ids: IDS, gems: GEMS, planGift, planGemsGift, createCoordinator };
  globalThis.CPPapayaPinkGift = api;
  if (typeof document === 'undefined') return;
  const root = window.Shopify?.routes?.root || '/';
  const rawFetch = window.fetch.bind(window);
  const cartPath = new URL(`${root}cart`, window.location.origin).pathname;
  let latestCart = null, latestSections = null, checkoutPending = false, replayingForm = null;
  let latestCartFingerprint = null;
  let initializedKeys = false;
  const errorId = 'cp-outubro-rosa-message';

  async function requestJson(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await rawFetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(data.description || 'Não foi possível conferir o brinde. Tente novamente.');
        error.status = response.status;
        throw error;
      }
      return data;
    } finally { clearTimeout(timeout); }
  }
  const writeGift = (action, body) => requestJson(`${root}cart/${action}.js`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body)
  });
  const coordinator = createCoordinator({
    gems: GEMS,
    readGemsAvailability: async () => {
      const product = await requestJson(`${root}products/${encodeURIComponent(GEMS.productHandle)}.js?_cp_gems=${Date.now()}`);
      const variant = product.variants?.find((item) => String(item.id) === GEMS.variantId);
      return variant?.available === true;
    },
    readCart: () => requestJson(`${root}cart.js`),
    updateGift: (updates) => writeGift('update', { updates }),
    addGift: (item) => writeGift('add', { items: [item] }),
    invalidated() { latestCart = null; latestSections = null; latestCartFingerprint = null; },
    observed(cart) {
      const fingerprint = JSON.stringify(cart.items.map((item) => [item.key, item.quantity, item.final_line_price]));
      if (fingerprint !== latestCartFingerprint) latestSections = null;
      latestCartFingerprint = fingerprint;
      latestCart = cart;
      // Liquid data attributes are authoritative. This fallback serves older section markup on first load.
      if (!initializedKeys) { annotateKeys(document, cart); initializedKeys = true; }
    }
  });

  function annotateKeys(scope, cart) {
    for (const input of scope.querySelectorAll('line-item-quantity [data-line]')) {
      const control = input.closest('line-item-quantity');
      const item = cart.items[Number(input.dataset.line) - 1];
      if (item?.key && !control.dataset.cpCartLineKey) control.dataset.cpCartLineKey = item.key;
    }
  }
  function lineKeys() {
    const keys = {};
    for (const input of document.querySelectorAll('line-item-quantity [data-line]')) {
      const key = input.closest('line-item-quantity')?.dataset.cpCartLineKey;
      if (key) keys[input.dataset.line] = key;
    }
    return keys;
  }
  function showMessage(message, blocking = false) {
    let node = document.getElementById(errorId);
    if (!message) { node?.remove(); return; }
    if (!node) {
      node = document.createElement('div');
      node.id = errorId;
      node.className = 'notification notification--error';
      node.style.cssText = 'position:fixed;z-index:100000;left:16px;right:16px;bottom:16px;max-width:620px;margin:auto;padding:16px;background:#fff4ef;color:#652e36;border:1px solid #ddb7be;box-shadow:0 4px 18px #0002';
      document.body.append(node);
    }
    node.setAttribute('role', blocking ? 'alert' : 'status');
    node.textContent = message;
  }
  function report(result) {
    if (result.error) showMessage('Não foi possível ajustar o brinde. Confira o carrinho antes de finalizar.', true);
    else if (result.unavailable) showMessage('A Alça Rosa Brinde está indisponível no momento. Seus produtos continuam no carrinho.');
    else showMessage('');
  }
  function mainSection() {
    return document.querySelector('.cart__recap')?.closest('[id^="shopify-section-"]');
  }
  async function sectionsFor(cart) {
    const main = mainSection();
    const ids = ['mini-cart'];
    if (main) ids.push(main.id.replace('shopify-section-', ''));
    const sections = await requestJson(`${root}cart?sections=${encodeURIComponent(ids.join(','))}`);
    for (const id of ids) {
      if (typeof sections[id] !== 'string' || !sections[id].trim()) throw new Error('Não foi possível atualizar o carrinho.');
      const template = document.createElement('template');
      template.innerHTML = sections[id];
      annotateKeys(template.content, cart);
      sections[id] = template.innerHTML;
    }
    latestSections = sections;
    return sections;
  }
  async function render(result, refreshMain = false) {
    if (!result.cart) return;
    const cart = result.cart;
    const sections = await sectionsFor(cart);
    cart.sections = sections;
    if (refreshMain) {
      const main = mainSection();
      if (main) {
        const template = document.createElement('template');
        template.innerHTML = sections[main.id.replace('shopify-section-', '')];
        const replacement = template.content.querySelector(`[id="${main.id}"]`);
        if (replacement) main.replaceWith(replacement);
      }
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart, cpPinkGift: true } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, cpPinkGift: true } }));
  }

  async function cartRequestArguments(input, options, keys, action) {
    if (!(input instanceof Request) && action !== 'change') return [input, options];
    const request = new Request(input, options);
    const contentType = request.headers.get('Content-Type') || '';
    const init = {
      method: request.method, headers: request.headers, credentials: request.credentials,
      cache: request.cache, mode: request.mode, redirect: request.redirect,
      referrer: request.referrer, referrerPolicy: request.referrerPolicy,
      integrity: request.integrity, keepalive: request.keepalive, signal: request.signal,
      body: /application\/json|application\/x-www-form-urlencoded/i.test(contentType)
        ? await request.clone().text() : await request.clone().blob()
    };
    if (action === 'change' && contentType.includes('application/json')) {
      const body = JSON.parse(init.body);
      if (body.line && !body.id && keys[body.line]) {
        body.id = keys[body.line];
        delete body.line;
        init.body = JSON.stringify(body);
      }
    }
    // Shopify's listener also reads the second fetch argument's body. Passing
    // only a Request breaks tracking when that listener was installed first.
    return [request.url, init];
  }

  // Focal's cart page reloads immediately after a change response, without emitting cart events.
  // This limited adapter settles only same-origin cart writes before releasing that response.
  window.fetch = function (input, options) {
    let url;
    try { url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href); }
    catch { return rawFetch(input, options); }
    const method = String(options?.method || input?.method || 'GET').toUpperCase();
    const action = ['add', 'change', 'update', 'clear'].find((name) => url.pathname === `${cartPath}/${name}.js`);
    if (url.origin !== window.location.origin || method !== 'POST' || !action) return rawFetch(input, options);
    const keys = lineKeys();
    // Convert relative inputs to absolute URLs for Request, preserving every other request option.
    const requestInput = typeof input === 'string' || input instanceof URL ? url.href : input;
    return coordinator.mutate(async () => rawFetch(...await cartRequestArguments(requestInput, options, keys, action)), async (result) => {
      report(result);
      const response = result.response;
      if (!response.ok || !result.cart || result.error) return response;
      try {
        const payload = await response.clone().json();
        if (!result.changed) {
          latestSections = payload.sections || null;
          return response;
        }
        const sections = await sectionsFor(result.cart);
        const body = action === 'add' ? { ...payload, sections } : { ...result.cart, sections };
        const headers = new Headers(response.headers);
        headers.delete('content-length');
        headers.delete('content-encoding');
        return retainResponseMetadata(new Response(JSON.stringify(body), { status: response.status, statusText: response.statusText, headers }), response);
      } catch {
        // Preserve the successful purchase request; a fresh cart page will reconcile on load.
        latestSections = null;
        showMessage('Carrinho atualizado. Abra o carrinho para conferir o brinde.');
        return response;
      }
    });
  };

  // Focal may emit a response it retained before a later write. Never render stale gift sections.
  const refreshCurrentCart = (event) => {
    if (!event.detail || !latestCart) return;
    if (event.type === 'cart:refresh') {
      // Focal delays removals by 600 ms and quantity edits by 750 ms. An older delayed
      // replacement can otherwise overwrite a newer gift result after a rapid add/remove.
      event.detail.replacementDelay = 0;
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.nextReplacementDelay = 0;
    }
    if (event.detail.cpPinkGift) return;
    event.detail.cart = { ...latestCart };
    if (latestSections) event.detail.cart.sections = latestSections;
  };
  document.addEventListener('cart:updated', refreshCurrentCart, true);
  document.addEventListener('cart:refresh', refreshCurrentCart, true);

  async function standardCheckout(proceed) {
    if (checkoutPending) return;
    checkoutPending = true;
    try {
      await coordinator.checkout(async (result) => { report(result); await proceed(); });
    } catch {
      showMessage('Não foi possível conferir o brinde. Atualize o carrinho e tente finalizar novamente.', true);
    } finally { checkoutPending = false; }
  }

  function canUseNativeCheckout() {
    const state = coordinator.state();
    if (state.pending || !state.cart || hasPromotionItems(state.cart)) return false;
    // Do not bypass a quantity edit or an older drawer that has not rendered
    // yet. Native submission must keep its original notes and updates[].
    const inputs = Array.from(document.querySelectorAll('line-item-quantity input[data-line]'));
    if (state.cart.items.length && !inputs.length) return false;
    return inputs.every((input) => {
      const key = input.closest('line-item-quantity')?.dataset.cpCartLineKey;
      const item = state.cart.items[Number(input.dataset.line) - 1];
      return item && item.key === key && Number(input.value) === Number(item.quantity);
    });
  }
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form === replayingForm || !(form instanceof HTMLFormElement)) return;
    const target = new URL(form.action, window.location.href);
    const checkout = event.submitter?.name === 'checkout' || Boolean(form.querySelector('input[name="checkout"]'));
    if (!checkout || target.origin !== window.location.origin || target.pathname.replace(/\/$/, '') !== cartPath) return;
    if (canUseNativeCheckout()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    standardCheckout(() => {
      // Focal may replace the drawer while an earlier cart write is settling.
      // Resolve the live form again; submitting the detached original silently does nothing.
      const currentForm = form.isConnected ? form :
        (form.id ? document.getElementById(form.id) : document.querySelector('form.cart__recap'));
      if (!(currentForm instanceof HTMLFormElement) || !currentForm.isConnected) {
        throw new Error('Abra o carrinho e tente finalizar novamente.');
      }
      if (currentForm !== form) {
        for (const field of Array.from(form.elements)) {
          if (field.disabled || !(field.name === 'note' || /^attributes\[[^\]]+\]$/.test(field.name))) continue;
          let currentField = Array.from(currentForm.elements).find((candidate) => candidate.name === field.name);
          if (!currentField) {
            currentField = document.createElement('input');
            currentField.type = 'hidden';
            currentField.name = field.name;
            currentForm.append(currentField);
          }
          currentField.value = field.value;
          if ('checked' in field) currentField.checked = field.checked;
        }
      }
      // Gift insertion changes Shopify's line ordering: never post stale index-based updates[].
      // Note/attributes stay on the form; quantity controls are saved separately by Focal's cart API.
      const updates = Array.from(currentForm.elements).filter((field) => /^updates(?:\[|$)/.test(field.name) && !field.disabled);
      updates.forEach((field) => { field.disabled = true; });
      const currentSubmitter = submitter?.isConnected && submitter.form === currentForm ? submitter :
        Array.from(currentForm.elements).find((field) => field.name === 'checkout' && field.type === 'submit');
      replayingForm = currentForm;
      try { currentForm.requestSubmit(currentSubmitter); }
      finally { replayingForm = null; updates.forEach((field) => { field.disabled = false; }); }
    });
  }, true);
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/i.test(url.pathname)) return;
    if (canUseNativeCheckout()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    standardCheckout(() => window.location.assign(url.href));
  }, true);

  api.sync = () => coordinator.sync(async (result) => {
    report(result);
    if (result.changed) await render(result, true);
    return result;
  });
  api.state = coordinator.state;
  const start = () => api.sync().catch(() => {
    if (latestCart?.items.some(isGift)) showMessage('Não foi possível ajustar o brinde. Confira o carrinho antes de finalizar.', true);
  }).finally(() => document.dispatchEvent(new CustomEvent('cp:outubro-rosa:ready')));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('pageshow', (event) => { if (event.persisted) start(); });
})();
